
import ffs, { FFS } from "kyofuuc";
import { CacheManager } from "../utils";
import { NoseurObject, Toaster, Scheme, MessageSchemesIcons } from "@rivtn/noseur";

export interface ApiResponse<T> {
    data?: T;
    message: string;
    success: boolean;
    secondary_data: NoseurObject<any>;
};

export type SanitizedResponse<T> = {
    sanitized: T;
    status: number;
    errors?: any[];
    errorMessage: string;
    apiResponse: ApiResponse<T>;
};

export interface IProfile {
    dob: string;
    email: string;
    phone: string;
    title: string;
    country: string;
    last_name: string;
    created_at: string;
    first_name: string;
    external_id: string;
    full_address: string;
    reports_to: IProfile;
    created_by: IProfile;
    profile_picture_location: string;
};

export class JanusGeminusService {

    // remove /json route after add formData submission on kyofuuc, and remove the headers this.config
    config: any = {
        headers: {
            Authorization: this.getAccessToken()
        }
    };
    transport: FFS;
    baseUrl: string = process.env.REACT_APP_JANUS_GEMINUS_BASE_API_URL ?? "http://172.20.10.4:9000";

    protected static instance: JanusGeminusService;

    constructor(baseUrl?: string) {
        this.transport = ffs.init({
            httpConfig: {
                baseURL: baseUrl ?? this.baseUrl,
                ...this.config
            }
        } as any);
    }

    static getInstance() {
        if (!JanusGeminusService.instance) JanusGeminusService.instance = new JanusGeminusService();
        return JanusGeminusService.instance;
    }

    getAccessToken() {
        const accessToken = CacheManager.get(CacheManager.ACCESS_TOKEN_KEY);
        if (!accessToken) return null;
        return `Bearer ${accessToken}`;
    }

    report<T>(promise: Promise<any>, postResolve?: Function, postReject?: Function): Promise<SanitizedResponse<any>> {
        return new Promise((resolve, reject) => {
            promise.then((response: T) => {
                let transformedRespomse = this.buildResponse(response);
                if (postResolve) postResolve(transformedRespomse);
                resolve(transformedRespomse);
            }).catch((error: any) => {
                let transformedError = this.buildError(error);
                if (postReject) postReject(transformedError);
                reject(transformedError);
                if (error.response?.status === 401) {
                    setTimeout(() => this.backToLogin(), 5000);
                }
            });
        });
    }

    buildResponse<T>(response: any): SanitizedResponse<T> {
        if (!response.data || !response.data.data) return response;
        response.status = response?.status;
        response.apiResponse = response.data;
        response.sanitized = response.apiResponse.data;
        return response;
    }

    buildError<T>(error: any): SanitizedResponse<T> {
        if (!error) error = {};
        error.message = error?.response?.data?.message ?? error?.response?.message ?? error?.message ?? "An error occur please try again later";
        error.errors = error?.response?.data?.errors;
        error.errorMessage = error.message;
        error.status = error.response?.status ?? -1;
        return error;
    }

    backToLogin() {
        CacheManager.clear();
        window.location = "/" as any;
    }

    // TODO use cache from kyofuuc with 1 hours expiry
    getProfile(refresh: boolean = false): Promise<SanitizedResponse<IProfile>> {
        return this.report(this.transport.get("/janus/geminus/api/v1/account"), ({ sanitized }: SanitizedResponse<IProfile>) => {
            CacheManager.put(CacheManager.PROFILE_KEY, sanitized);
        });
    }

    static reportError<T>(response: SanitizedResponse<T>) {
        Toaster.toast({
            lifetime: 5000,
            foreScheme: true,
            scheme: Scheme.DANGER,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
            icon: MessageSchemesIcons.INFO,
            content: response?.errorMessage ?? `${response}`,
        });
    }

}
